import React from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { formatCurrency, formatNumber, formatPercentage } from '@tracekit/core';

const COLORS = {
  primary: '#0ea5e9',
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#6366f1',
  purple: '#8b5cf6',
  pink: '#ec4899',
  teal: '#14b8a6',
};

const PIE_COLORS = [
  COLORS.primary,
  COLORS.success,
  COLORS.warning,
  COLORS.error,
  COLORS.info,
  COLORS.purple,
  COLORS.pink,
  COLORS.teal,
];

// Custom Tooltip Component
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  valueFormatter?: (value: number) => string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, valueFormatter }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        <p className="font-semibold text-gray-900 dark:text-gray-100 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {valueFormatter ? valueFormatter(entry.value) : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// 1. Pipeline by Stage Chart
interface PipelineStageData {
  stage: string;
  count: number;
  value: number;
}

export const PipelineStageChart: React.FC<{ data: PipelineStageData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis type="number" />
        <YAxis dataKey="stage" type="category" width={100} />
        <Tooltip content={<CustomTooltip valueFormatter={(v) => formatNumber(v)} />} />
        <Legend />
        <Bar dataKey="count" fill={COLORS.primary} name="Opportunities" />
        <Bar dataKey="value" fill={COLORS.success} name="Value ($M)" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// 2. Win Rate Trend Chart
interface WinRateTrendData {
  month: string;
  rate: number;
  proposals: number;
}

export const WinRateTrendChart: React.FC<{ data: WinRateTrendData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="month" />
        <YAxis
          yAxisId="left"
          label={{ value: 'Win Rate (%)', angle: -90, position: 'insideLeft' }}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          label={{ value: 'Proposals', angle: 90, position: 'insideRight' }}
        />
        <Tooltip />
        <Legend />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey="rate"
          stroke={COLORS.success}
          name="Win Rate (%)"
          strokeWidth={2}
          dot={{ fill: COLORS.success, r: 4 }}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="proposals"
          stroke={COLORS.info}
          name="Proposals"
          strokeWidth={2}
          dot={{ fill: COLORS.info, r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// 3. Revenue Projection Chart
interface RevenueProjectionData {
  month: string;
  actual: number;
  projected: number;
}

export const RevenueProjectionChart: React.FC<{ data: RevenueProjectionData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="month" />
        <YAxis label={{ value: 'Revenue ($M)', angle: -90, position: 'insideLeft' }} />
        <Tooltip content={<CustomTooltip valueFormatter={(v) => formatCurrency(v)} />} />
        <Legend />
        <Area
          type="monotone"
          dataKey="actual"
          stroke={COLORS.success}
          fill={COLORS.success}
          fillOpacity={0.6}
          name="Actual Revenue"
        />
        <Area
          type="monotone"
          dataKey="projected"
          stroke={COLORS.primary}
          fill={COLORS.primary}
          fillOpacity={0.3}
          strokeDasharray="5 5"
          name="Projected Revenue"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// 4. Staff Allocation Chart
interface StaffAllocationData {
  name: string;
  value: number;
}

export const StaffAllocationChart: React.FC<{ data: StaffAllocationData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip valueFormatter={(v) => formatNumber(v)} />} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

// 5. Client Satisfaction Chart
interface ClientSatisfactionData {
  client: string;
  satisfaction: number;
  projects: number;
}

export const ClientSatisfactionChart: React.FC<{ data: ClientSatisfactionData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="client" angle={-45} textAnchor="end" height={80} />
        <YAxis label={{ value: 'Satisfaction (%)', angle: -90, position: 'insideLeft' }} />
        <Tooltip content={<CustomTooltip valueFormatter={(v) => `${v}%`} />} />
        <Legend />
        <Bar dataKey="satisfaction" fill={COLORS.success} name="Satisfaction Score" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// 6. Service Line Distribution Chart
interface ServiceLineDistributionData {
  name: string;
  value: number;
  revenue?: number;
}

export const ServiceLineDistributionChart: React.FC<{ data: ServiceLineDistributionData[] }> = ({
  data,
}) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
          outerRadius={80}
          fill="#8884d8"
          dataKey="value"
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          content={
            <CustomTooltip
              valueFormatter={(v) => `${formatNumber(v)} projects${data[0]?.revenue ? `, ${formatCurrency(data[0].revenue)}` : ''}`}
            />
          }
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
};

// 7. Opportunity Pipeline Trend Chart
interface OpportunityTrendData {
  month: string;
  active: number;
  new: number;
  closed: number;
}

export const OpportunityTrendChart: React.FC<{ data: OpportunityTrendData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="month" />
        <YAxis label={{ value: 'Opportunities', angle: -90, position: 'insideLeft' }} />
        <Tooltip content={<CustomTooltip valueFormatter={(v) => formatNumber(v)} />} />
        <Legend />
        <Line
          type="monotone"
          dataKey="active"
          stroke={COLORS.primary}
          name="Active"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="new"
          stroke={COLORS.success}
          name="New"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="closed"
          stroke={COLORS.error}
          name="Closed"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// 8. Compliance Status Chart
interface ComplianceStatusData {
  category: string;
  compliant: number;
  nonCompliant: number;
  highRisk: number;
}

export const ComplianceStatusChart: React.FC<{ data: ComplianceStatusData[] }> = ({ data }) => {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="category" />
        <YAxis label={{ value: 'Requirements', angle: -90, position: 'insideLeft' }} />
        <Tooltip content={<CustomTooltip valueFormatter={(v) => formatNumber(v)} />} />
        <Legend />
        <Bar dataKey="compliant" stackId="a" fill={COLORS.success} name="Compliant" />
        <Bar dataKey="nonCompliant" stackId="a" fill={COLORS.warning} name="Non-Compliant" />
        <Bar dataKey="highRisk" stackId="a" fill={COLORS.error} name="High Risk" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// Export all chart types
export const DashboardCharts = {
  PipelineStageChart,
  WinRateTrendChart,
  RevenueProjectionChart,
  StaffAllocationChart,
  ClientSatisfactionChart,
  ServiceLineDistributionChart,
  OpportunityTrendChart,
  ComplianceStatusChart,
};

